import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Connection } from '../obj/Connection';
import { StorageRouterService } from '../services/storage-router.service';
import { FlowManagementService } from '../services/flow-management.service';

/**
 * Route landing for `/connections/:envName/:connectionName/edit`.
 *
 * Shows the Connection JSON editor for a specific tenant connection. The
 * sister-route `/connector/:envName/:connectorName/edit` shows the *connector*
 * (API definition); we expose a "Connector" tab here that navigates there for
 * convenience when the user is looking at a specific connection and wants to
 * inspect the shared connector definition behind it.
 */
@Component({
    selector: 'app-connection-edit',
    templateUrl: './connection-edit.component.html',
    styleUrls: ['./connection-edit.component.scss'],
    standalone: false,
})
export class ConnectionEditComponent implements OnInit, OnDestroy {
    public connection: Connection | null = null;
    public error = '';
    public loading = true;

    private subscription = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private storage: StorageRouterService,
        private flowManagementService: FlowManagementService,
    ) {}

    async ngOnInit(): Promise<void> {
        const envName = this.route.snapshot.paramMap.get('envName') || '';
        const connectionName = this.route.snapshot.paramMap.get('connectionName') || '';
        if (!envName || !connectionName) {
            this.error = 'Missing connection route parameters.';
            this.loading = false;
            return;
        }

        // Set the active env so other services that read currentEnvironment
        // resolve correctly when this page is opened directly (deep-link / refresh).
        this.flowManagementService.currentEnvironment = envName;

        // Cache-first: the connections page primes the cache via putConnections,
        // so revisits and deep-links should resolve instantly.
        const cached = await this.storage.getConnections(envName).catch(() => [] as Connection[]);
        this.connection = (cached || []).find((c) =>
            c.name === connectionName || c.id?.endsWith(`/${connectionName}`)
        ) || null;

        if (!this.connection) {
            this.error = `Connection "${connectionName}" not found in cache for this environment. Open it from the Connections page first.`;
        }
        this.loading = false;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public backToConnections(): void {
        this.router.navigate(['/connections']);
    }

    /** Cross-link: jump to the connector (API definition) view for this connection. */
    public viewConnector(): void {
        if (!this.connection?.environment || !this.connection?.connector) return;
        this.router.navigate(['/connector', this.connection.environment, this.connection.connector, 'edit']);
    }
}
